import { useState, useCallback, useMemo } from 'react'
import { agents } from '../../../../../api/agents'

const POLL_MS = 1500
const SETTLED_THREAD_STATUSES = new Set(['done', 'error', 'cancelled', 'ended'])
const TERMINAL_REPORT_STATUSES = new Set(['complete', 'inconclusive', 'error'])

export function useInvestigation () {
  const [sessionId, setSessionId] = useState(null)
  const [startError, setStartError] = useState(null)

  const invokeMutation = agents.useInvokePortfolioResearch()

  const pollQuery = agents.usePortfolioResearchReport(sessionId, {
    refetchInterval: (data) => {
      if (!data) return POLL_MS
      return isSettled(data) ? false : POLL_MS
    }
  })

  const start = useCallback(async (bundle, subject) => {
    setStartError(null)
    setSessionId(null)
    try {
      const data = await invokeMutation.mutateAsync({ action: 'start', bundle, subject })
      const id = data?.portfolioResearchReportSessionId
      if (!id) throw new Error('No portfolioResearchReportSessionId returned from start')
      setSessionId(id)
      return id
    } catch (err) {
      setStartError(err)
      throw err
    }
  }, [invokeMutation])

  const openExisting = useCallback((existingSessionId) => {
    setStartError(null)
    setSessionId(existingSessionId ?? null)
  }, [])

  const answerQuestions = useCallback(async (answers) => {
    if (!sessionId) return
    await invokeMutation.mutateAsync({
      action: 'answer_questions',
      portfolioResearchReportSessionId: sessionId,
      answers
    })
    pollQuery.refetch()
  }, [sessionId, invokeMutation, pollQuery])

  const continueRun = useCallback(async () => {
    if (!sessionId) return
    await invokeMutation.mutateAsync({
      action: 'continue',
      portfolioResearchReportSessionId: sessionId
    })
    pollQuery.refetch()
  }, [sessionId, invokeMutation, pollQuery])

  const reset = useCallback(() => {
    setSessionId(null)
    setStartError(null)
  }, [])

  const view = useMemo(() => deriveView({
    starting: invokeMutation.isLoading && !sessionId,
    startError,
    pollError: pollQuery.error,
    report: pollQuery.data,
    sessionId
  }), [invokeMutation.isLoading, sessionId, startError, pollQuery.error, pollQuery.data])

  return {
    sessionId,
    report: pollQuery.data ?? null,
    view,
    isStarting: invokeMutation.isLoading && !sessionId,
    isActing: invokeMutation.isLoading,
    startError,
    pollError: pollQuery.error,
    start,
    openExisting,
    answerQuestions,
    continueRun,
    reset
  }
}

function isSettled (report) {
  return SETTLED_THREAD_STATUSES.has(report?.threadStatus)
}

function deriveView ({ starting, startError, pollError, report, sessionId }) {
  if (startError) return { kind: 'start_failed', error: startError }
  if (starting) return { kind: 'starting' }
  // pollError before !report so a failed initial load on a known sessionId
  // surfaces as 'poll_failed' instead of an infinite 'idle' spinner.
  if (sessionId != null && pollError && !report) return { kind: 'poll_failed', error: pollError }
  if (!report) return { kind: 'idle' }
  if (pollError) return { kind: 'poll_failed', error: pollError }

  if (report.threadStatus === 'pending_action') {
    return { kind: 'needs_input', report }
  }
  if (TERMINAL_REPORT_STATUSES.has(report.status)) {
    return { kind: 'complete', report }
  }
  // Trust the server's canContinue rather than re-deriving retryability from
  // thread/status here. Server-side it's: thread settled + report not terminal
  // + not awaiting input.
  if (report.canContinue) {
    return { kind: 'stuck', report }
  }
  return { kind: 'investigating', report }
}
